import { PlacementId } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { handleEvent } from "util/user-events";
import { AiSummaryState } from "./ai-summary-state";
import { useEntityAiSummary } from "./use-entity-ai-summary";

export const useClientAiSummary = (clientId: PlacementId): AiSummaryState => {
    const trpcClient = trpc.useTRPC();

    const summaryQuery = useQuery({
        ...trpcClient.clientAiSummary.find.queryOptions({ entityId: clientId }),
    });
    const generateMutation = useMutation({
        ...trpcClient.clientAiSummary.generate.mutationOptions(),
    });
    const submitFeedbackMutation = useMutation({
        ...trpcClient.clientAiSummary.submitFeedback.mutationOptions(),
    });
    const updateTextMutation = useMutation({
        ...trpcClient.clientAiSummary.updateText.mutationOptions(),
    });
    const removeSummaryMutation = useMutation({
        ...trpcClient.clientAiSummary.removeSummary.mutationOptions(),
    });

    return useEntityAiSummary({
        summary: summaryQuery.data,
        isQueryLoading: summaryQuery.isLoading,
        queryError: summaryQuery.error?.message,
        refetchSummary: () => summaryQuery.refetch(),

        triggerGenerate: () => generateMutation.mutateAsync({ entityId: clientId }),
        isGenerating: generateMutation.isPending,
        generateError: generateMutation.error?.message,

        triggerSubmitFeedback: input => submitFeedbackMutation.mutate(input),
        isSubmittingFeedback: submitFeedbackMutation.isPending,
        submitFeedbackError: submitFeedbackMutation.error?.message,

        triggerUpdateText: input => updateTextMutation.mutateAsync(input),
        updateTextError: updateTextMutation.error?.message,

        triggerRemoveSummary: input => removeSummaryMutation.mutateAsync(input),
        isRemoving: removeSummaryMutation.isPending,
        removeSummaryError: removeSummaryMutation.error?.message,

        trackGenerate: () =>
            handleEvent("ai-summary-generate", { clientId, isRegenerate: false }),
        trackRegenerate: () =>
            handleEvent("ai-summary-generate", { clientId, isRegenerate: true }),
    });
};
